import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Row, Col, Form, Nav, Tab, Button, OverlayTrigger, Tooltip, Alert } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import Modal from "../modal";
import CsLineIcons from "../../cs-line-icons";
import { get as getPersonSettings } from "../../actions/person.settings";
import { send as sendEmail, suggest as suggestEmail } from "../../actions/email.template";

const EMPTY_BODY = "<p><br></p><p><br></p>";

const functions = {
    now: () => new Date().toLocaleString(),
};

const resolveToken = (token, context) => {
    const value = String(token ?? "").trim();
    if (!value) return "";

    if (value.startsWith("$")) return value.slice(1);

    if (value.startsWith("@")) {
        const fn = functions[value.slice(1)];
        return fn ? fn() : "";
    }

    const path = value.split(".");
    let current = context;

    while (current && path.length > 0) {
        current = current[path.shift()];
    }

    return typeof current === "string" ? current : "";
};

const resolveRecipients = (template, context, fallback = "") => {
    if (!template) return String(fallback || "");

    const source = String(template || "");

    return source
        .split(",")
        .map((token) => resolveToken(token, context))
        .filter(Boolean)
        .join(", ");
};

class EmailComposeModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            to: "",
            cc: "",
            bcc: "",
            subject: "",
            body: EMPTY_BODY,
            suggestion: "",
            showSuggestionTabs: false,
            activeTab: "body",
            suggesting: false,
            sending: false,
            aiRules: "",
            settings: {},
            loaded: false,
            attachments: [],
            attaching: false,
            attachmentError: "",
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show) {
            this.loadData();
        }
    }

    loadData = async () => {
        const result = await this.props.getPersonSettings();
        const settings = result?.settings || {};
        const context = {
            ...(this.props.context || {}),
            person: this.props.person || this.props.context?.person || {},
        };
        const signature = settings.EMAIL_SIGNATURE || "";

        this.setState({
            settings,
            to: resolveRecipients(settings.EMAIL_TO, context, this.props.to),
            cc: resolveRecipients(settings.EMAIL_CC, context),
            bcc: resolveRecipients(settings.EMAIL_BCC, context),
            subject: settings.EMAIL_SUBJECT || "",
            body: `${EMPTY_BODY}${signature}`,
            aiRules: settings.EMAIL_AI_RULES || "",
            loaded: true,
            attachments: [],
            attaching: false,
            attachmentError: "",
        });
    };

    onChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    };

    onBodyChange = (body) => {
        this.setState({ body });
    };

    onTabChange = (activeTab) => {
        this.setState({ activeTab });
    };

    getValue = () => ({
        to: this.state.to,
        cc: this.state.cc,
        bcc: this.state.bcc,
        subject: this.state.subject,
        body: this.state.body,
        configuration: this.state.settings.EMAIL_CONFIGURATION || "",
        from: this.state.settings.EMAIL_SENDER || "",
        display: this.state.settings.EMAIL_DISPLAY || "",
        aiRules: this.state.aiRules,
        settings: this.state.settings,
        attachments: this.state.attachments,
    });

    readAttachment = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || "");
            const comma = result.indexOf(",");
            if (comma < 0) {
                reject(new Error(`Unable to read ${file.name}.`));
                return;
            }
            resolve({
                filename: file.name,
                content: result.slice(comma + 1),
                encoding: "base64",
                contentType: file.type || "application/octet-stream",
                size: file.size,
            });
        };
        reader.onerror = () => reject(new Error(`Unable to read ${file.name}.`));
        reader.readAsDataURL(file);
    });

    onAttachmentsChange = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (files.length === 0) return;

        this.setState({ attaching: true, attachmentError: "" });
        try {
            const attachments = await Promise.all(files.map(this.readAttachment));
            this.setState((prev) => ({
                attachments: [...prev.attachments, ...attachments],
            }));
        } catch (error) {
            this.setState({ attachmentError: error?.message || "Unable to add one or more attachments." });
        } finally {
            this.setState({ attaching: false });
        }
    };

    onAttachmentRemove = (index) => {
        if (this.state.sending || this.state.attaching) return;
        this.setState((prev) => ({
            attachments: prev.attachments.filter((_, attachmentIndex) => attachmentIndex !== index),
            attachmentError: "",
        }));
    };

    onSend = async () => {
        if (this.state.sending) return;

        const value = this.getValue();

        this.setState({ sending: true });
        try {
            const result = await this.props.sendEmail({
                configuration: value.configuration,
                from: value.from,
                display: value.display,
                to: value.to,
                cc: value.cc,
                bcc: value.bcc,
                subject: value.subject,
                body: value.body,
                attachments: value.attachments,
            });

            await this.props.onSend?.(value, result);
            this.onCancel();
        } finally {
            this.setState({ sending: false });
        }
    };

    onCancel = () => {
        this.props.onCancel?.();
    };

    onSuggest = async () => {
        const value = this.getValue();

        this.setState({ suggesting: true });
        try {
            const result = await this.props.suggestEmail({
                body: value.body,
                rules: value.aiRules,
                subject: value.subject,
                to: value.to,
                cc: value.cc,
                bcc: value.bcc,
            });

            this.setState({
                suggestion: result?.suggestion || "",
                showSuggestionTabs: true,
                activeTab: "suggestion",
            });

            this.props.onSuggest?.(this.getValue());
        } finally {
            this.setState({ suggesting: false });
        }
    };

    onAcceptSuggestion = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();

        this.setState((prev) => ({
            body: prev.suggestion,
            suggestion: "",
            showSuggestionTabs: false,
            activeTab: "body",
        }));
    };

    renderEditor = () => {
        if (!this.state.showSuggestionTabs) {
            return (
                <ReactQuill
                    theme="snow"
                    value={this.state.body}
                    onChange={this.onBodyChange}
                />
            );
        }

        return (
            <Tab.Container activeKey={this.state.activeTab} onSelect={this.onTabChange}>
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="body">Body</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="suggestion">
                            <span>Suggestion</span>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Use suggestion</Tooltip>}
                            >
                                <Button
                                    type="button"
                                    variant="link"
                                    className="p-0 ms-2 align-baseline"
                                    onClick={this.onAcceptSuggestion}
                                >
                                    <CsLineIcons icon="check" width={16} height={16} />
                                </Button>
                            </OverlayTrigger>
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="body">
                        <ReactQuill
                            theme="snow"
                            value={this.state.body}
                            onChange={this.onBodyChange}
                        />
                    </Tab.Pane>
                    <Tab.Pane eventKey="suggestion">
                        <ReactQuill
                            theme="snow"
                            value={this.state.suggestion}
                            readOnly
                            modules={{ toolbar: false }}
                        />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        );
    };

    render() {
        return (
            <Modal
                show={this.props.show}
                size="lg"
                title="New Email"
                buttons={[
                    {
                        caption: this.state.sending ? "sending" : "send",
                        onEnabledClick: this.onSend,
                        variant: "primary",
                        enabled: !this.state.sending && !this.state.attaching,
                        display: true,
                    },
                    {
                        default: true,
                        caption: "cancel",
                        onEnabledClick: this.onCancel,
                        variant: "primary",
                        enabled: true,
                        display: true,
                    },
                    {
                        caption: this.state.suggesting ? "suggesting" : "suggest",
                        onEnabledClick: this.onSuggest,
                        variant: "secondary",
                        enabled: !this.state.suggesting,
                        display: true,
                    },
                ]}
            >
                <Row className="g-3 mt-1">
                    <Col xs="12">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="to"
                                value={this.state.to}
                                onChange={this.onChange}
                            />
                            <Form.Label>TO</Form.Label>
                        </div>
                    </Col>
                    <Col xs="12" md="6">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="cc"
                                value={this.state.cc}
                                onChange={this.onChange}
                            />
                            <Form.Label>CC</Form.Label>
                        </div>
                    </Col>
                    <Col xs="12" md="6">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="bcc"
                                value={this.state.bcc}
                                onChange={this.onChange}
                            />
                            <Form.Label>BCC</Form.Label>
                        </div>
                    </Col>
                    <Col xs="12">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="subject"
                                value={this.state.subject}
                                onChange={this.onChange}
                            />
                            <Form.Label>SUBJECT</Form.Label>
                        </div>
                    </Col>
                    <Col xs="12">
                        {this.renderEditor()}
                    </Col>
                    <Col xs="12">
                        <Form.Group>
                            <Form.Label>Attachments</Form.Label>
                            <Form.Control
                                type="file"
                                multiple
                                disabled={this.state.sending || this.state.attaching}
                                onChange={this.onAttachmentsChange}
                            />
                            <Form.Text className="text-muted">
                                {this.state.attaching ? "Adding attachments..." : "Select one or more files to include with this email."}
                            </Form.Text>
                        </Form.Group>
                        {this.state.attachments.length > 0 ? (
                            <div className="mt-2">
                                {this.state.attachments.map((attachment, index) => (
                                    <div
                                        key={`${attachment.filename}-${index}`}
                                        className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2"
                                    >
                                        <div className="text-truncate me-3">
                                            <CsLineIcons icon="attachment" width={16} height={16} className="me-2" />
                                            <span title={attachment.filename}>{attachment.filename}</span>
                                            <small className="text-muted ms-2">
                                                {Math.max(1, Math.ceil(Number(attachment.size || 0) / 1024)).toLocaleString()} KB
                                            </small>
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline-danger"
                                            disabled={this.state.sending || this.state.attaching}
                                            onClick={() => this.onAttachmentRemove(index)}
                                            aria-label={`Remove ${attachment.filename}`}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {this.state.attachmentError ? (
                            <Alert variant="danger" className="mt-2 mb-0">
                                {this.state.attachmentError}
                            </Alert>
                        ) : null}
                    </Col>
                </Row>
            </Modal>
        );
    }
}

EmailComposeModal.propTypes = {
    show: PropTypes.bool,
    to: PropTypes.string,
    context: PropTypes.object,
    person: PropTypes.object,
    getPersonSettings: PropTypes.func.isRequired,
    sendEmail: PropTypes.func.isRequired,
    suggestEmail: PropTypes.func.isRequired,
    onSend: PropTypes.func,
    onCancel: PropTypes.func,
    onSuggest: PropTypes.func,
};

export default connect(null, { getPersonSettings, sendEmail, suggestEmail })(EmailComposeModal);
